/* eslint-disable */
/**
 * Enhanced API Utility Functions
 * Includes offline support, caching, and background sync
 */

import { API_CONFIG } from '../config/api.config';
import { toast } from "react-hot-toast";
import offlineDB from './offlineDB';
import axios from "axios";

const buildApiError = ({
  type = "http",
  status = null,
  statusText = "",
  endpoint = "",
  method = "GET",
  responseData = null,
  cause = null,
}) => {
  const backendMessage =
    responseData?.error ||
    responseData?.message ||
    responseData?.reply ||
    "";

  let userMessage = "Request failed. Please try again.";
  if (type === "network") userMessage = "Cannot reach the server. Check that it is running.";
  if (status === 400) userMessage = backendMessage || "Invalid request.";
  if (status === 401) userMessage = "Unauthorized access.";
  if (status === 403) userMessage = "Access forbidden.";
  if (status === 404) userMessage = "Requested resource not found.";
  if (status === 409) userMessage = backendMessage || "Conflict.";
  if (status === 422) userMessage = backendMessage || "Request could not be processed.";
  if (status >= 500) userMessage = backendMessage || "Server error. Please try again later.";

  const error = new Error(userMessage);
  error.name = "ApiError";
  error.type = type;
  error.status = status;
  error.statusText = statusText;
  error.endpoint = endpoint;
  error.method = method;
  error.responseData = responseData;
  error.technicalMessage = backendMessage || cause?.message || "";
  error.isApiError = true;
  return error;
};

/**
 * Ensures requests only target the configured Flask API base URL.
 * Blocks absolute URLs so the browser never calls UISP/UNMS or other third-party APIs directly.
 */
const assertRelativeBackendPath = (pathSegment) => {
  const p = String(pathSegment ?? "").trim();
  if (!p) return;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(p) || p.startsWith("//")) {
    const err = new Error(
      "API paths must be relative to the application backend only; direct UISP or other external API URLs are not allowed in the frontend."
    );
    err.name = "InvalidApiPathError";
    throw err;
  }
};

// Create a simple fetch instance without offline support
const fetchInstance = async (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const hasBody = options.body !== undefined && options.body !== null && options.body !== "";
  const baseHeaders = {
    'X-API-Key': API_CONFIG.apiKey,
    ...options.headers,
  };
  const headers = hasBody && !baseHeaders['Content-Type']
    ? { ...baseHeaders, 'Content-Type': 'application/json' }
    : baseHeaders;

  // Remove any leading slash from the URL to prevent double slashes
  const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
  assertRelativeBackendPath(cleanUrl);
  const fullUrl = `${API_CONFIG.baseUrl}/${cleanUrl}`;

  // Log request details
  console.group('🌐 API Request');
  console.log('URL:', fullUrl);
  console.log('Method:', method);
  console.log('Headers:', {
    ...headers,
    'X-API-Key': '***' // Hide API key in logs
  });
  if (options.body) {
    try {
      const bodyData = JSON.parse(options.body);
      console.log('Request Body:', bodyData);
    } catch (e) {
      console.log('Request Body:', options.body);
    }
  }
  console.groupEnd();

  try {
    const response = await fetch(fullUrl, {
      ...options,
      method,
      headers,
    });

    // Log response details
    console.group('📥 API Response');
    console.log('Status:', response.status);
    console.log('Status Text:', response.statusText);
    
    // For 204 NO CONTENT, return the response object directly
    if (response.status === 204) {
      console.log('Response: No Content (204)');
      console.groupEnd();
      return response;
    }
    
    let responseData = null;
    const rawText = await response.text();
    if (rawText) {
      try {
        responseData = JSON.parse(rawText);
      } catch {
        responseData = { message: rawText };
      }
    }
    console.log('Response Data:', responseData);
    console.groupEnd();

    if (!response.ok) {
      throw buildApiError({
        type: "http",
        status: response.status,
        statusText: response.statusText,
        endpoint: cleanUrl,
        method,
        responseData,
      });
    }

    return responseData;
  } catch (error) {
    // Log error details
    console.group('❌ API Error');
    console.error('Error:', error);
    console.error('Request URL:', fullUrl);
    console.error('Request Method:', options.method || 'GET');
    if (options.body) {
      try {
        const bodyData = JSON.parse(options.body);
        console.error('Request Body:', bodyData);
      } catch (e) {
        console.error('Request Body:', options.body);
      }
    }
    console.groupEnd();
    
    if (error?.isApiError) {
      throw error;
    }

    throw buildApiError({
      type: "network",
      endpoint: cleanUrl,
      method,
      cause: error,
    });
  }
};

// Export API functions
export const getApiKey = () => API_CONFIG.apiKey;

export const get = (url, options = {}) => fetchInstance(url, { method: 'GET', ...options });

export const post = (url, data) => 
  fetchInstance(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const put = (url, data) =>
  fetchInstance(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const del = (url) => fetchInstance(url, { method: 'DELETE' });

// Update ticket priority level via generic tickets PUT (no /level sub-route on backend).
export const updateTicketLevel = async (ticketId, newLevel) => {
  try {
    const updateData = {
      level: newLevel,
      update_datetime: new Date().toISOString().slice(0, 19).replace("T", " "),
    };
    const data = await put(`/db/tickets/${ticketId}`, updateData);
    toast.success(`Ticket level updated to ${newLevel}`);
    return { success: true, data };
  } catch (error) {
    console.error('Failed to update ticket level:', error);
    toast.error('Failed to update ticket level');
    throw error;
  }
};

// Export empty offline functions that do nothing
export const initOfflineDB = () => Promise.resolve();
export const storeApiResponse = () => Promise.resolve();
export const getCachedApiResponse = () => Promise.resolve(null);
export const clearOfflineDB = () => Promise.resolve();

// Online/Offline status management
let isOnline = navigator.onLine;
const onlineListeners = new Set();

window.addEventListener('online', () => {
  isOnline = true;
  processSyncQueue(); // Try to sync when coming back online
  onlineListeners.forEach(listener => listener(true));
});

window.addEventListener('offline', () => {
  isOnline = false;
  onlineListeners.forEach(listener => listener(false));
});

// Subscribe to online/offline changes
export const subscribeToOnlineStatus = (listener) => {
  onlineListeners.add(listener);
  return () => onlineListeners.delete(listener);
};

// Sync queue management with error handling and retries
const addToSyncQueue = async (action) => {
  if (!isOnline) {
    try {
      await offlineDB.store('syncQueue', {
        ...action,
        timestamp: Date.now(),
        retryCount: 0
      });
      return true;
    } catch (error) {
      console.error('Failed to add to sync queue:', error);
      return false;
    }
  }
  return false;
};

const processSyncQueue = async () => {
  if (!isOnline) {
    console.log('Offline - skipping sync queue processing');
    return;
  }

  try {
    const actions = await offlineDB.getAll('syncQueue');
    
    if (!actions || actions.length === 0) {
      console.log('No pending actions to process');
      return;
    }

    for (const action of actions) {
      try {
        await fetchInstance(action.url, {
          method: action.method,
          headers: action.headers,
          body: action.data,
        });
        
        await offlineDB.delete('syncQueue', action.id);
        
        if (!API_CONFIG.isProduction) {
          console.log('✅ Synced action:', action);
        }
      } catch (error) {
        console.error('❌ Sync failed for action:', action, error);
        
        // Update retry count and timestamp
        const updatedAction = {
          ...action,
          retryCount: (action.retryCount || 0) + 1,
          lastError: error.message,
          timestamp: Date.now()
        };
        
        await offlineDB.store('syncQueue', updatedAction);
      }
    }
  } catch (error) {
    console.error('Failed to process sync queue:', error);
  }
};

// Enhanced API call function with offline support
export const apiCall = async (endpoint, options = {}) => {
  const { 
    method = "GET", 
    data = null, 
    params = null, 
    headers = {},
  } = options;

  // Remove any leading slash from the endpoint
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

  // Make network request
  try {
    const responseData = await fetchInstance(cleanEndpoint, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined
    });

    return responseData;
  } catch (error) {
    throw error;
  }
};

// Add uploadFormData function
const uploadFormData = async (endpoint, formData, options = {}) => {
  try {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    assertRelativeBackendPath(cleanEndpoint);
    const fullUrl = `${API_CONFIG.baseUrl}/${cleanEndpoint}`;

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'X-API-Key': API_CONFIG.apiKey,
        ...options.headers,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

/**
 * Download a file from the API
 * @param {string} endpoint - The API endpoint to download from
 * @param {Object} options - Download options
 * @param {string} options.filename - Optional filename for the download
 * @param {string} options.mimeType - Optional MIME type for the download
 * @returns {Promise} - Resolves when download is complete
 */
const downloadFile = async (endpoint, options = {}) => {
  try {
    const { filename, mimeType } = options;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    assertRelativeBackendPath(cleanEndpoint);
    const fullUrl = `${API_CONFIG.baseUrl}/${cleanEndpoint}`;

    const response = await axios({
      method: "GET",
      url: fullUrl,
      responseType: "blob",
      headers: {
        'X-API-Key': API_CONFIG.apiKey,
        ...(mimeType && { Accept: mimeType }),
        ...options.headers,
      },
    });

    if (!response.data) {
      throw new Error("No data received from server");
    }

    const blob = new Blob([response.data], { type: response.headers["content-type"] });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "download";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => window.URL.revokeObjectURL(url), 100);

    return response;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

// Enhanced convenience functions
export const api = {
  get: async (endpoint, params = null, options = {}) => {
    return apiCall(endpoint, { 
      method: "GET", 
      params,
      ...options 
    });
  },

  post: async (endpoint, data, options = {}) => {
    return apiCall(endpoint, { 
      method: "POST", 
      data,
      ...options 
    });
  },

  put: async (endpoint, data, options = {}) => {
    return apiCall(endpoint, { 
      method: "PUT", 
      data,
      ...options 
    });
  },

  delete: async (endpoint, options = {}) => {
    return apiCall(endpoint, { 
      method: "DELETE",
      ...options 
    });
  },

  uploadFormData,
  downloadFile,  // Add downloadFile to the api object

  // Check online status
  isOnline: () => isOnline,

  // Force sync queue processing
  sync: () => processSyncQueue(),

  // Clear cache for a specific endpoint
  clearCache: async (endpoint) => {
    return offlineDB.delete(endpoint);
  },

  // Get pending sync actions
  getPendingActions: async () => {
    return offlineDB.getAll('syncQueue');
  }
};

export default api;
